"use client";
import { handleMaybeProgressing } from "@/hooks/gamification/monitoring/progressing";
import { callGradeFlashcardSetStudySession, callResetStudySessionSorting } from "@/hooks/study/graphqlUtils";
import { withSide } from "@/hooks/study/utils";
import { now } from "@/utils/dateTimeUtils";
import { fromEntries, wait } from "@/utils/genericUtils";
import { FlashcardWithSide } from "@knowt/syncing/graphql/customSchema";
import {
    Flashcard,
    FlashcardSet,
    FlashcardSetViewer,
    FlashcardSide,
    FlashcardSortingEnum,
    FlashcardStudyState,
    GradeFlashcardSetStudySessionQuestionInput,
    QuestionType,
    StudySession,
    StudySessionType,
} from "@knowt/syncing/graphql/schema";
import { useFlashcardSetViewer } from "@knowt/syncing/hooks/flashcardSetViewer/useFlashcardSetViewer";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import {
    EnqueuedQuestionSubmission,
    useFlashcardStudyStates,
    useStudySession,
} from "@knowt/syncing/hooks/study/useStudySession";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import { isEqual } from "lodash-es";
import omit from "lodash/omit";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KeyedMutator, mutate } from "swr";

type ReviewModeContextValue = {
    flashcardSet?: FlashcardSet;
    flashcardSetViewerData: FlashcardSetViewer;
    updateFlashcard: (
        flashcardId: string,
        data: Partial<Flashcard> | ((oldFlashcard: Flashcard) => Partial<Flashcard>)
    ) => void;
    isShowingEndScreen: boolean;
    showSignupCard: boolean;
    setFlashcardIndex: (n: number) => void;
    starProps: {
        starredFlashcardIds?: string[] | null;
        toggleFlashcardStarById: (id?: string) => void;
    };
    readOnly: boolean;
    updateStudySessionViewer: (viewer: { isShuffled: boolean; position?: number; order?: string[] }) => void;
    commitViewerUpdates: () => Promise<void>;
    isViewerDirty: boolean;

    flashcards: FlashcardWithSide[];
    numOfCardsInCarousel: number;
    flashcardIndex: number;
    isLoading: boolean;

    // SORTING MODE
    sortedKnow: number | undefined;
    sortedDontKnow: number | undefined;
    commitSortingUpdates: () => Promise<StudySession>;
    isSortingMode: boolean;
    isSortingDirty: boolean;
    onSort: (sort: FlashcardSortingEnum) => void;
    canRevertSort: boolean;
    onRevertSort: () => void;
    onResetSort: ({ onlyForStillLearning }: { onlyForStillLearning?: boolean }) => void;
} & ReturnType<typeof useStudySession>;

type ReviewModeContextProps = {
    flashcardSet?: FlashcardSet | undefined;
    flashcardSetId: string;
    studySession?: StudySession | undefined | null;
    children: React.ReactNode;
};

const ReviewModeContext = createContext<ReviewModeContextValue | null>(null);

const useSubmitSortingHelpers = ({
    flashcardSet,
    mutateStudySession,
    loading,
    error,
}: {
    flashcardSet?: FlashcardSet | null;
    mutateStudySession: KeyedMutator<StudySession>;
    loading: boolean;
    error: Error;
}) => {
    const flashcardSetId = flashcardSet?.flashcardSetId;
    const { userId } = useCurrentUser();

    const pendingUpdates = useRef<(GradeFlashcardSetStudySessionQuestionInput & { submitted?: boolean })[]>([]);
    const lastCommit = useRef<number>(null);

    const [lastSubmitted, setLastSubmitted] = useState<number>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isCommittingUpdates, setIsCommittingUpdates] = useState(false);

    const commitUpdates = useCallback(async () => {
        if (!userId) {
            setIsDirty(false);
            return;
        }

        if (loading || error) return;

        setIsCommittingUpdates(true);

        const commitStartTime = Date.now();
        lastCommit.current = commitStartTime;

        const pendingQuestions = pendingUpdates.current;
        pendingUpdates.current = [];

        let updatedStudySession: StudySession = undefined;

        if (pendingQuestions.length) {
            // console.log(`Committing ${pendingQuestions.length} sorting updates`, pendingQuestions);
            const result = await callGradeFlashcardSetStudySession({
                flashcardSetId,
                questions: pendingQuestions.map(question => omit(question, "submitted")),
            });

            await handleMaybeProgressing(result);

            const {
                item: { studySession, studyStates },
            } = result;

            // since dynamodb writes are eventually consistent, we need to wait for the write to be consistent
            // before updating the cache
            await wait(1000);

            updatedStudySession = studySession;

            const updatedStudyStatesMap = fromEntries(
                studyStates.map(studyState => [studyState.flashcardId, studyState])
            );

            await mutate(
                ["flashcard-study-states", userId, flashcardSetId],
                async (data: Record<string, FlashcardStudyState>) => ({
                    ...data,
                    ...updatedStudyStatesMap,
                }),
                { revalidate: false }
            );

            await mutateStudySession(updatedStudySession, false);
        }

        setLastSubmitted(Date.now());

        const isLastCommit = lastCommit.current === commitStartTime;
        const noPendingUpdates = !pendingUpdates.current.length;

        if (isLastCommit && noPendingUpdates) setIsDirty(false);

        setIsCommittingUpdates(false);

        return updatedStudySession;
    }, [error, flashcardSetId, mutateStudySession, loading, userId]);

    const enqueueQuestionSubmission = useCallback((question: EnqueuedQuestionSubmission, override = false) => {
        // if the last question is the same as the current question, replace it
        const insertedIndex = pendingUpdates.current.findIndex(
            ({ flashcardId }) => flashcardId === question.flashcardId
        );

        if (insertedIndex !== -1) {
            // replace the time taken with added time
            // the time taken gets set when the answer is submitted, then added onto when they advance to the next question. There may be a small gap where pending updates gets committed before the time taken is updated, and we will lose the time taken for there, but it's not a big deal.
            pendingUpdates.current[insertedIndex] = {
                ...pendingUpdates.current[insertedIndex],
                ...question,
                isCorrect:
                    (override
                        ? question.isCorrect
                        : pendingUpdates.current[insertedIndex].isCorrect !== false && question.isCorrect) || false,
                timeTaken: pendingUpdates.current[insertedIndex].timeTaken + question.timeTaken,
            };
        } else if (!question.submitted) {
            // if it's already submitted, don't submit it again
            pendingUpdates.current.push({
                ...question,
                isCorrect: question.isCorrect || false,
            });
        }

        setIsDirty(true);
    }, []);

    return {
        lastSubmitted,
        isDirty,
        commitUpdates,
        isCommittingUpdates,
        enqueueQuestionSubmission,
    };
};

export const ReviewModeContextProvider = ({
    flashcardSet: serverFlashcardSet,
    flashcardSetId,
    children,
}: ReviewModeContextProps) => {
    const { userId, user } = useCurrentUser();

    const studySessionProps = useStudySession({
        flashcardSetId,
        type: StudySessionType.REVIEW,
    });

    const { flashcardStudyStates, isLoading, mutate: mutateStudyStates } = useFlashcardStudyStates({ flashcardSetId });

    const studySession = studySessionProps.studySession;
    const settings = studySession?.settings?.REVIEW;
    const isSortingMode = settings?.sorting;
    const updateSettings = studySessionProps.updateSettings;

    const { flashcardSet, updateFlashcard } = useFlashcardSet({ flashcardSetId, fallbackData: serverFlashcardSet });

    const flashcardsWithSide = useMemo(() => {
        const cards = flashcardSet?.flashcards?.map(card => ({
            ...card,
            side: withSide(settings?.answerSide ?? FlashcardSide.DEFINITION),
        }));

        return cards;
    }, [flashcardSet?.flashcards, settings]);

    const {
        flashcardSetViewerData,
        updateStudySessionViewer: _updateStudySessionViewer,
        flashcardIndex: normalFlashcardIndex,
        setFlashcardIndex: _setFlashcardIndex,
        toggleFlashcardStarByIds,
        commitUpdates: commitViewerUpdates,
        isDirty: isViewerDirty,
    } = useFlashcardSetViewer({ flashcardSetId });

    const setFlashcardIndex = useCallback(
        (n: number) => {
            if (isSortingMode) {
                // pass, we set this manually through onSort
            } else {
                _setFlashcardIndex(n);
            }
        },
        [isSortingMode, _setFlashcardIndex]
    );

    const [flashcardSortingIndex, setFlashcardSortingIndex] = useState(undefined);

    // casting because if it's not null/undefined, it has to be a string array
    // and cannot be a (string|null) array as the type `initialFlashcardIndex[] | null | undefined` suggests
    const starred = flashcardSetViewerData?.starred as string[] | null | undefined;
    const order = flashcardSetViewerData?.order;

    const normalFlashcards = useMemo(() => {
        let cards = flashcardsWithSide || [];
        if (order?.length) {
            const flashcardMap =
                flashcardsWithSide?.reduce(
                    (acc, curr) => {
                        // shouldn't happen, but just for TS to be happy
                        if (!curr?.flashcardId) return acc;
                        acc[curr.flashcardId] = curr;
                        return acc;
                    },
                    {} as Record<string, FlashcardWithSide>
                ) || {};

            cards = order
                .map(id => {
                    // shouldn't happen, but just for TS to be happy
                    if (!id) return null;
                    return flashcardMap[id];
                })
                .filter(Boolean) as FlashcardWithSide[];
        }

        if (settings?.starred && starred?.length === 0) {
            // TODO: this belongs to an effect not in the body of the component!!!
            updateSettings({ starred: false });
        }

        // TODO: set index to first card that is not sorted

        return cards.filter(card => {
            if (!card) return false;
            return settings?.starred ? starred?.includes(card?.flashcardId) : true;
        });
    }, [flashcardsWithSide, order, settings, starred]);

    const numOfFlashcards = normalFlashcards?.length ?? 0;
    const showSignupCard = !user && numOfFlashcards >= 4;
    const normalNumOfCardsInCarousel = numOfFlashcards + (showSignupCard ? 0 : 1);

    const flashcardsSample = useMemo(() => normalFlashcards.slice(0, 5), [normalFlashcards]);

    /**
     * SORTING MODE CODE
     */

    const {
        isDirty,
        commitUpdates: commitSortingUpdates,
        enqueueQuestionSubmission,
    } = useSubmitSortingHelpers({
        flashcardSet,
        mutateStudySession: studySessionProps.mutateStudySession,
        loading: !studySession,
        error: null,
    });

    const [sortingFlashcards, setSortingFlashcards] = useState<FlashcardWithSide[]>(normalFlashcards);
    const [triggerRecalculateCards, setTriggerRecalculateCards] = useState(0);
    const lastSortedFlashcards = useRef<FlashcardWithSide[]>([]);

    const flashcards = isSortingMode ? sortingFlashcards : !showSignupCard ? normalFlashcards : flashcardsSample;
    const flashcardIndex = isSortingMode ? flashcardSortingIndex : normalFlashcardIndex;

    const prevOrder = useRef<string[]>([]);

    useEffect(() => {
        if (
            prevOrder.current?.length &&
            isEqual(
                prevOrder.current,
                normalFlashcards.map(f => f.flashcardId)
            )
        ) {
            const flashcardsMap = fromEntries(normalFlashcards.map(f => [f.flashcardId, f]));
            // if everything is the same, pass. we only recalculate cards if the order or length changes
            setSortingFlashcards(prev =>
                prev.map(f => ({
                    ...f,
                    ...flashcardsMap[f.flashcardId],
                }))
            );
            return;
        }

        recalculateCards();
    }, [isLoading, normalFlashcards]);

    useEffect(() => {
        recalculateCards();
    }, [triggerRecalculateCards]);

    const recalculateCards = () => {
        if (isLoading || !flashcardStudyStates || !Object.keys(flashcardStudyStates).length) return;

        const remainingCards = normalFlashcards.filter(f => !flashcardStudyStates[f.flashcardId]?.sort);
        prevOrder.current = normalFlashcards.map(f => f.flashcardId);

        if (remainingCards.length !== 0) {
            if (remainingCards.length !== sortingFlashcards.length) {
                lastSortedFlashcards.current = [];
            }

            setSortingFlashcards(remainingCards);
            setFlashcardSortingIndex(0);
        } else {
            setFlashcardSortingIndex(normalFlashcards.length);
        }
    };

    const sortedKnow =
        !normalFlashcards || !flashcardStudyStates || !Object.keys(flashcardStudyStates).length
            ? undefined
            : normalFlashcards.filter(f => flashcardStudyStates[f.flashcardId]?.sort === FlashcardSortingEnum.KNOW)
                  ?.length;

    const sortedDontKnow =
        !normalFlashcards || !flashcardStudyStates || !Object.keys(flashcardStudyStates).length
            ? undefined
            : normalFlashcards.filter(f => flashcardStudyStates[f.flashcardId]?.sort === FlashcardSortingEnum.D_KNOW)
                  ?.length;

    const numOfCardsInCarousel = isSortingMode ? sortingFlashcards.length + 1 : normalNumOfCardsInCarousel;

    const currentFlashcard = sortingFlashcards?.[flashcardSortingIndex];

    const onSort = useCallback(
        async (sort: FlashcardSortingEnum) => {
            if (!currentFlashcard) return;

            enqueueQuestionSubmission({
                flashcardId: currentFlashcard.flashcardId,
                side: currentFlashcard.side,
                timestamp: now(),
                mode: StudySessionType.REVIEW,
                isCorrect: sort === FlashcardSortingEnum.KNOW,
                questionType: QuestionType.NONE,
                answer: "",
                sort,
                submitted: false,
                timeTaken: 0,
            });
            lastSortedFlashcards.current.push(currentFlashcard);
            setFlashcardSortingIndex(prev => {
                const newIndex = prev + 1;
                if (newIndex >= numOfCardsInCarousel - 1) {
                    commitSortingUpdates();
                }

                return newIndex;
            });

            // mutate the cache
            await mutateStudyStates(
                prev => {
                    prev[currentFlashcard.flashcardId].sort = sort;

                    return prev;
                },
                { revalidate: false }
            );
        },
        [
            currentFlashcard,
            flashcardStudyStates,
            commitSortingUpdates,
            enqueueQuestionSubmission,
            sortingFlashcards,
            mutateStudyStates,
        ]
    );

    const onRevertSort = useCallback(async () => {
        const lastSortedFlashcard = lastSortedFlashcards.current.pop();
        if (!lastSortedFlashcard) return;
        if (flashcardSortingIndex === 0) return;

        enqueueQuestionSubmission({
            flashcardId: lastSortedFlashcard.flashcardId,
            side: lastSortedFlashcard.side,
            timestamp: now(),
            mode: StudySessionType.REVIEW,
            isCorrect: null,
            questionType: QuestionType.NONE,
            answer: "",
            sort: null,
            submitted: false,
            timeTaken: 0,
        });
        setFlashcardSortingIndex(prev => prev - 1);

        await mutateStudyStates(
            prev => {
                prev[lastSortedFlashcard.flashcardId].sort = null;

                return prev;
            },
            { revalidate: false }
        );
    }, [mutateStudyStates, enqueueQuestionSubmission, flashcardSortingIndex]);

    const onResetSort = useCallback(
        async ({
            onlyForStillLearning,
        }: {
            onlyForStillLearning?: boolean;
        }) => {
            await commitSortingUpdates();
            callResetStudySessionSorting({
                flashcardSetId,
                onlyForStillLearning,
            });
            setFlashcardSortingIndex(0);
            lastSortedFlashcards.current = [];

            await mutateStudyStates(
                prev => {
                    // go through all records, and remove the sort
                    for (const flashcardId in prev) {
                        // dont knows are always reset, if onlyForStillLearning is false, we reset all
                        if (!onlyForStillLearning || prev[flashcardId]?.sort === FlashcardSortingEnum.D_KNOW) {
                            prev[flashcardId].sort = null;
                        }
                    }

                    return prev;
                },
                { revalidate: false }
            );
            setTriggerRecalculateCards(prev => prev + 1);
        },
        [commitSortingUpdates, flashcardSetId, flashcardStudyStates, mutateStudyStates]
    );

    const value = useMemo(
        () => ({
            flashcardSet: flashcardSet ?? undefined,
            flashcardSetViewerData,
            flashcards,
            showSignupCard,
            readOnly: flashcardSet?.userId !== userId || !!flashcardSet?.lock,
            updateFlashcard,
            isShowingEndScreen: (flashcardIndex || 0) >= numOfCardsInCarousel - 1,
            numOfCardsInCarousel,
            // in the case that someone turns on starred mode while they were in the middle of a set
            flashcardIndex: Math.min(flashcardIndex ?? 0, numOfCardsInCarousel - 1),
            setFlashcardIndex,
            starProps: {
                starredFlashcardIds: starred,
                toggleFlashcardStarById: (id: string) => id && toggleFlashcardStarByIds([id]),
            },
            studySession: studySession ?? undefined,
            updateStudySessionViewer: ({ isShuffled, position, order }) =>
                _updateStudySessionViewer({
                    isShuffled,
                    position,
                    order,
                    flashcards: flashcardSet?.flashcards,
                }),
            ...studySessionProps,
            isLoading: isSortingMode
                ? isLoading || flashcardIndex === undefined
                : !flashcardSet || !flashcardSetViewerData || !flashcards,
            commitViewerUpdates,
            isViewerDirty,

            // SORTING
            sortedKnow,
            sortedDontKnow,
            commitSortingUpdates,
            isSortingMode,
            isSortingDirty: isDirty,
            onSort,
            canRevertSort: lastSortedFlashcards.current.length > 0,
            onRevertSort,
            onResetSort,
        }),
        [
            studySessionProps,
            _updateStudySessionViewer,
            commitViewerUpdates,
            flashcardIndex,
            flashcardSet,
            flashcardSetViewerData,
            flashcards,
            isViewerDirty,
            numOfCardsInCarousel,
            setFlashcardIndex,
            showSignupCard,
            starred,
            studySession,
            toggleFlashcardStarByIds,
            updateFlashcard,
            userId,
            commitSortingUpdates,
            onResetSort,
            sortedDontKnow,
            onSort,
            isSortingMode,
            sortedKnow,
            isDirty,
            onRevertSort,
            isLoading,
        ]
    );

    return <ReviewModeContext.Provider value={value}>{children}</ReviewModeContext.Provider>;
};

export const useReviewModeContextSelector = <T,>(selector: (value: ReviewModeContextValue) => T) =>
    useContextSelector(ReviewModeContext, selector);
